import {
  AdminKycActionRequiredPageSchema,
  AdminKycSyncResultSchema,
} from '@entities/admin-user/model/kyc-verification'
export * from '@entities/admin-user/model/kyc-verification'
export { useAdminKycVerification } from '@entities/admin-user/api/kyc-verification'
import { useMemo } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { DashboardPeriod } from '../lib/admin-dashboard-period'
import { apiFetch, apiFetchBlob } from '@shared/api/http'
import { AppError } from '@shared/api/error'

export type MoneyDto = {
  amount: string
  currency: string
}

export type MetricRow = {
  currency: string
  amount: MoneyDto
  count: number
  convertedCount?: number
  unconvertedCount?: number
}

export type ProductSalesMetric = {
  productId: string
  productName: string
  currency: string
  amount: MoneyDto
  count: number
}

export type FinancialCacheMetadata = { computedAt: string; hit: boolean }

export type AdminDashboard = {
  cache?: FinancialCacheMetadata
  payments: {
    totalSales: MetricRow[]
    affiliateSales: MetricRow[]
    coProducerSales: MetricRow[]
    chargebacks: MetricRow[]
    refunds: MetricRow[]
    globalChargebackRefunds: MetricRow[]
    paymentMethods: Array<{
      currency: string
      paymentMethod: string
      amount: MoneyDto
      count: number
    }>
    global: {
      paidCount: number
      failedCount: number
      pendingCount: number
      refundedCount: number
      chargedBackCount: number
    }
  }
  settlement: {
    saleFee: MetricRow[]
    currencyConversionFee: MetricRow[]
    saleCurrencyConversionFee: MetricRow[]
    withdrawalCurrencyConversionFee: MetricRow[]
    platformRevenue: MetricRow[]
    receivablesByRole: Array<{ currency: string; role: string; amount: MoneyDto; count: number }>
    receivablesByCurrency: Array<{
      currency: string
      amount: MoneyDto
      count: number
    }>
  }
}

export type SaleFinancialEvent = {
  fx?: {
    referenceId: string
    originalBasis: MoneyDto
    providerDebit: MoneyDto
    loss: MoneyDto
    originalTransactionId: string
    transactionIds: string[]
  }
  kind:
    | 'PARTIAL_REFUND'
    | 'REFUND'
    | 'PREVENTED_CHARGEBACK'
    | 'CHARGEBACK'
    | 'DISPUTE_WON'
    | 'FX_LOSS'
  state: 'RECOVERING' | 'RECOVERED' | 'REVERSED'
  chargeAffectedAmount?: MoneyDto
  affectedAmount: MoneyDto
  recoveredAmount: MoneyDto
  recoveryVerified?: boolean
  returnedAmount?: MoneyDto
  canceledAmount?: MoneyDto
  feeReturnedAmount?: MoneyDto
  feeCanceledAmount?: MoneyDto
  outstandingAmount: MoneyDto
  occurredAt: string
  feeAmount?: MoneyDto
  feeRecoveredAmount?: MoneyDto
  feeOutstandingAmount?: MoneyDto
}

export type AdminSaleLine = {
  saleId: string
  lineId: string
  kind: string
  offerId: string
  offerName?: string
  productId: string
  productName?: string
  amount: MoneyDto
  settlementAmount?: MoneyDto
  split: unknown
}

export type SaleCoupon = {
  code: string
  type: 'percent' | 'fixed'
  percent?: number
  configuredAmount?: MoneyDto
  amountBeforeDiscount: MoneyDto
  discountAmount: MoneyDto
  settlement?: {
    amountBeforeDiscount: MoneyDto
    discountAmount: MoneyDto
  }
}

export type SaleItem = {
  id: string
  productId: string
  productName?: string
  chargeType?: 'CHECKOUT' | 'UPSELL' | 'DOWNSELL' | 'FUNNEL' | 'RENEWAL'
  journeyId?: string
  originSaleId?: string
  producerId: string
  sellerId: string
  affiliateId?: string
  coProducerId?: string
  seedId?: string
  buyerName: string
  buyerEmail?: string
  coupon?: SaleCoupon
  amountPaid: MoneyDto
  refundableAmount?: MoneyDto
  grossAmount: MoneyDto
  commercialAmount?: MoneyDto
  fxVariation?: MoneyDto
  serviceFeeQuote?: {
    policy: 'UNIVERSAL_USD_FIXED_V1' | 'CUSTOM_USD_FIXED_V1'
    percentage: string
    sourceAmount: string
    sourceCurrency: 'USD'
    currency: string
    fixedAmount: string
    rate: string
    quotedAt: string
    expiresAt: string
    providerQuoteId?: string
    rateKind: string
  }
  buyerPaidConversion?: boolean
  providerSettlementAmount?: MoneyDto
  providerFeeAmount?: MoneyDto
  settlementAmount?: MoneyDto
  reportingAmountBrl?: MoneyDto
  reportingAmountUsd?: MoneyDto
  exchangeRate?: string
  exchangeRateSource?: string
  exchangeRateQuotedAt?: string
  exchangeRateExpiresAt?: string
  exchangeRateProviderQuoteId?: string
  exchangeRateKind?: string
  status: string
  paymentMethod?: string
  gateway?: string
  gatewayPaymentId?: string
  gatewayRefundId?: string
  gatewayDisputeId?: string
  handledByGlobalChargeback: boolean
  isRenewal: boolean
  globalMarket: boolean
  createdAt: string
  paidAt?: string
  refundedAt?: string
  financialEvents?: SaleFinancialEvent[]
  lines?: AdminSaleLine[]
}

export type LedgerItem = {
  id: string
  saleId: string
  type: string
  recipientId?: string
  lineId?: string
  offerId?: string
  kind?: string
  phase?: string
  amount: MoneyDto
  availableAt: string | null
  availability?: { date: string | null; status: string; source: string; cardCountry: string | null }
  createdAt: string
  status: string
  source: string
  role?: string
  appliedPercentage?: string
  appliedFixedFee?: MoneyDto
  feeMode?: 'STANDARD' | 'MICRO'
  feePlanId?: string
  baseAmount?: MoneyDto
  withdrawalId?: string
  debtId?: string
  receivingPeriod?: string
}

export type StatementJournalItem = {
  id: string
  recipientId: string
  occurredAt: string
  eventType:
    | 'SALE_SETTLED'
    | 'SALE_REVOKED'
    | 'DEBT_CREATED'
    | 'DEBT_SETTLED'
    | 'DEBT_REVERSED'
    | 'WITHDRAWAL_REQUESTED'
    | 'WITHDRAWAL_APPROVED'
    | 'WITHDRAWAL_REFUSED'
    | 'WITHDRAWAL_COMPLETED'
    | 'WITHDRAWAL_REVERTED'
    | 'WITHDRAWAL_OBSERVED'
    | 'CONVERSION'
  saleId?: string
  relatedSaleIds: string[]
  amount: MoneyDto
  direction: 'IN' | 'OUT' | 'HELD' | 'INFO'
  currency: string
  detail: Record<string, unknown>
  eventId: string
  correlationId?: string
  createdAt: string
}

export type AdminStatementItem = LedgerItem | StatementJournalItem
export type AdminStatementView = 'journal' | 'ledger'

export type BalanceItem = {
  currency: string
  available: MoneyDto
  pending: MoneyDto
  awaitingAvailability?: MoneyDto
  reserve: MoneyDto
  openDebt: MoneyDto
  processingWithdrawal: MoneyDto
  processingDebtRecovery: MoneyDto
  nextReleases?: Array<{ date: string; amount: MoneyDto; count?: number }>
}

export type CurrencySummaryItem = {
  currency: string
  count: number
}

export type SubscriptionItem = {
  commercialAmount?: MoneyDto
  id: string
  planId: string
  productId: string
  producerId: string
  buyerEmail?: string
  buyerName?: string
  affiliateId?: string
  affiliateRecurring?: boolean
  coProducerId?: string
  seedId?: string
  seedPercentage?: string
  initialSaleId: string
  initialSaleAvailable?: boolean
  status: string
  chargeAmount?: MoneyDto
  settlementAmount?: MoneyDto
  recurringAmount?: MoneyDto
  billingPeriodUnit?: 'MINUTE' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'
  billingPeriodCount?: number
  cancelAtPeriodEnd?: boolean
  currentPeriodEndAt: string
  paymentMethod: string
  gateway: 'STRIPE' | 'MERCADO_PAGO' | 'PAYPAL'
  createdAt: string
}

export type AdminRuntimeCapabilities = {
  qaToolsEnabled: boolean
  minuteBillingEnabled: boolean
  recuperaAiEnabled: boolean
}

export type SubscriptionTestInvoice = {
  id: string
  cycleN: number
  status: string
  dueAt: string
  chargeAmount: MoneyDto
  settlementSaleId: string
  gatewayPaymentId?: string
}

export type SubscriptionTestContext = {
  subscription: {
    id: string
    status: string
    buyerName?: string
    buyerEmail?: string
    productId: string
    planId: string
    gateway: string
    currentPeriodStartAt: string
    currentPeriodEndAt: string
    cancelAtPeriodEnd: boolean
  }
  nextInvoice?: SubscriptionTestInvoice
  eligibility: {
    canAdvance: boolean
    reason?: string
  }
}

export type SubscriptionTestResult = {
  outcome:
    | 'SUCCEEDED'
    | 'FAILED'
    | 'REQUIRES_ACTION'
    | 'BLOCKED'
    | 'AMBIGUOUS'
    | 'ALREADY_PROCESSED'
    | 'SKIPPED'
  subscription: SubscriptionTestContext['subscription']
  processedInvoice: SubscriptionTestInvoice
  nextInvoice?: SubscriptionTestInvoice
  asynchronousEffects: 'PENDING' | 'NOT_STARTED'
}

export type ProductItem = {
  id: string
  producerId: string
  name: string
  description?: string
  status: string
  productType: string
  price: MoneyDto
  supportEmail?: string
  supportName?: string
  salesPageUrl?: string
  maxInstallments?: number
  refundDays?: number
  offers: Array<{ id: string; name: string; price: MoneyDto; status: string }>
  checkouts: Array<{ id: string; name: string; status: string; link: string }>
  recurrencePlans: Array<{ id: string; name: string; value: MoneyDto; active: boolean }>
}

export type AdminLookupUser = {
  id: string
  email: string
  name?: string
  emailVerified: boolean
  documentationStatus: string
  isSaleBlocked: boolean
  createdAt: string
}

export type AdminUserProfile = {
  phone?: string
  firstName?: string
  lastName?: string
  birthDate?: string
  country?: string
  businessType?: string
  documentType?: string
  documentNumber?: string
  politicalExposure?: string
  businessName?: string
  legalName?: string
  monthlyRevenue?: number
  businessProfile?: {
    mcc?: string
    productDescription?: string
    url?: string
  }
  representative?: {
    documentNumber: string
    title: string
  }
  address?: {
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
  createdAt?: string
  updatedAt?: string
}

export type AdminUserDetail = AdminLookupUser & {
  profile: AdminUserProfile | null
}

export type AcquirerValidationCheck = {
  key:
    | 'AUTHENTICATION'
    | 'ACCOUNT'
    | 'CREDENTIAL_PAIR'
    | 'CHARGES'
    | 'PAYOUTS'
    | 'APPLE_PAY'
    | 'GOOGLE_PAY'
    | 'WEBHOOK'
  status: 'PASSED' | 'FAILED' | 'WARNING'
  message: string
}

export type AcquirerAccount = {
  id: string
  provider: 'STRIPE'
  name: string
  providerAccountId: string
  publishableKey: string
  mode?: 'TEST' | 'LIVE' | 'UNKNOWN'
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'DEGRADED' | 'DISABLED'
  healthStatus: 'PENDING' | 'HEALTHY' | 'DEGRADED' | 'UNAVAILABLE'
  acceptsNewTransactions: boolean
  capabilities: Record<string, unknown>
  validationReport: AcquirerValidationCheck[]
  balances: Array<{ kind: 'AVAILABLE' | 'PENDING'; amount: string; currency: string }>
  globalPercentage: number
  credentialsConfigured: boolean
  webhookSecretConfigured: boolean
  lastHealthCheckedAt?: string
  createdAt: string
  updatedAt: string
}

export type DivergenceItem = {
  divergenceId: string
  scope: string
  saleId?: string
  recipientId?: string
  reason: string
  expectedAmount: MoneyDto
  observedAmount?: MoneyDto
  status: string
  detectedAt: string
}

export type AccountingOverview = {
  transactions: number
  postings: number
  accounts: number
  unbalanced: number
  reversals: number
  appendOnly: boolean
  receivableLots: number
  receivableEvents: number
  subledgerAppendOnly: boolean
  legacyLedgerRemoved: boolean
  tolerance: string
}

export type AccountingTransaction = {
  id: string
  eventId: string
  eventType: string
  sourceType: string
  sourceId: string
  correlationId: string
  causationId?: string
  currency: string
  effectiveAt: string
  recordedAt: string
  settledAt?: string
  createdBy: string
  reason?: string
  reversalOf?: string
  metadata: Record<string, unknown>
  integrityHash: string
  postingsCount: number
  debits: MoneyDto
  credits: MoneyDto
  balanced: boolean
}

export type AccountingPosting = {
  id: string
  accountId: string
  accountCode: string
  accountName: string
  accountCategory: string
  normalSide: 'DEBIT' | 'CREDIT'
  side: 'DEBIT' | 'CREDIT'
  amount: MoneyDto
  participantId?: string
  saleId?: string
  withdrawalId?: string
  debtId?: string
  sourceEntryId?: string
  lineId?: string
  metadata: Record<string, unknown>
}

export type AccountingTransactionDetail = AccountingTransaction & {
  integrityValid: boolean
  postings: AccountingPosting[]
}

export type AccountingBalance = {
  accountId: string
  code: string
  name: string
  category: string
  normalSide: 'DEBIT' | 'CREDIT'
  currency: string
  ownerId?: string
  balance: MoneyDto
  postingsCount: number
}

export type OperationalAlertItem = {
  id: string
  key: string
  severity: 'WARN' | 'CRITICAL'
  status: 'OPEN' | 'ACKED' | 'RESOLVED'
  summary: string
  fields: Record<string, unknown>
  source: string
  firstSeenAt: string
  lastSeenAt: string
  occurrenceCount: number
  acknowledgedAt?: string
  acknowledgedBy?: string
  resolvedAt?: string
  resolvedBy?: string
  resolutionNote?: string
  createdAt: string
  updatedAt: string
}

export type OperationalAlertSummary = {
  open: number
  acknowledged: number
  criticalOpen: number
  warnOpen: number
}

export type GatewayWebhookItem = {
  id: string
  gateway: 'STRIPE' | 'MERCADO_PAGO' | 'PAYPAL'
  gatewayEventId: string
  payload: unknown
  receivedAt: string
  status: 'PENDING' | 'PROCESSED' | 'SKIPPED' | 'SKIPPED_FOREIGN' | 'DEAD'
  ownership?: 'OWNED' | 'FOREIGN' | 'UNKNOWN'
  attempts: number
  nextAttemptAt: string
  lastError?: string
  createdAt: string
  updatedAt: string
}

export type AdminEventItem = {
  id: string
  type: string
  version: number
  payload: unknown
  occurredAt: string
  producer: string
  correlationId: string
  causationId?: string
  status: 'PENDING' | 'PROCESSING' | 'DONE' | 'DEAD'
  attempts: number
  createdAt: string
}

export type ChargebackCaseItem = {
  id: string
  provider: string
  provider_event_id: string
  provider_alert_id?: string | null
  status: string
  alert_occurred_at?: string | null
  amount: string
  currency: string
  network?: string | null
  gateway?: string | null
  gateway_payment_id?: string | null
  authorization_code?: string | null
  card_last4?: string | null
  card_brand?: string | null
  transaction_at?: string | null
  sale_id?: string | null
  reason?: string | null
  callback_attempts: number
  created_at: string
  updated_at: string
}

export type StaffItem = {
  id: string
  email: string
  name: string
  status: 'ACTIVE' | 'INACTIVE'
  isSuper: boolean
  permissions: string[]
  createdAt: string
  updatedAt: string
}

type Page<T> = {
  items: T[]
  nextCursor: string | null
  hasMore: boolean
}

function qs(params: Record<string, string | number | boolean | string[] | undefined>) {
  const entries = Object.entries(params).flatMap(([key, value]) => {
    if (value === undefined || value === '') return []
    if (Array.isArray(value)) return value.map((v) => [key, v] as [string, string])
    return [[key, String(value)]] as [string, string][]
  })
  const str = new URLSearchParams(entries).toString()
  return str ? `?${str}` : ''
}

export type AdminDashboardParams = {
  period?: DashboardPeriod
  comparison?: 'previous'
  currency?: string
  participantId?: string
  productId?: string
  dateFrom?: string
  dateTo?: string
}

/** Metade do dashboard consumida pela tela de desempenho do usuário. */
export type AdminDashboardPayments = Pick<AdminDashboard, 'payments'>

export type AdminSalesSeriesPoint = {
  bucket: string
  amount: MoneyDto
  previousAmount: MoneyDto
  count: number
  previousCount: number
}

export type SeriesGranularity = 'hour' | 'day' | 'month'

export type AdminSalesSeries = {
  cache?: FinancialCacheMetadata
  granularity: SeriesGranularity
  from: string
  to: string
  series: { currency: string; points: AdminSalesSeriesPoint[] }[]
}

/**
 * Não repete a consulta quando o servidor falha, pelo mesmo motivo do dashboard: o retry
 * automático multiplicava a carga justamente quando o banco já estava saturado.
 */
export function useAdminDashboard(params: AdminDashboardParams, enabled = true) {
  return useQuery({
    queryKey: ['admin-dashboard', params],
    queryFn: () => apiFetch<AdminDashboard>(`/admin/dashboard${qs(params)}`),
    enabled,
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof AppError && error.status >= 500),
  })
}

export function useAdminSalesSeries(params: AdminDashboardParams, enabled = true) {
  return useQuery({
    queryKey: ['admin-sales-series', params],
    queryFn: () => apiFetch<AdminSalesSeries>(`/admin/dashboard/sales-series${qs(params)}`),
    enabled,
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof AppError && error.status >= 500),
  })
}

/**
 * Métricas de um participante. Pede apenas a seção `payments`, a única que a tela lê, e
 * não repete a consulta quando o servidor falha: no incidente de saturação do banco o
 * retry automático multiplicava a carga justamente quando ela já estava alta.
 */
export function useAdminParticipantDashboard(params: AdminDashboardParams, enabled = true) {
  const query = { ...params, sections: 'payments' }
  return useQuery({
    queryKey: ['admin-dashboard', query],
    queryFn: () => apiFetch<AdminDashboardPayments>(`/admin/dashboard${qs(query)}`),
    enabled,
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof AppError && error.status >= 500),
  })
}

export function useAdminProductSales(
  params: AdminDashboardParams & { participantId: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['admin-dashboard-products', params],
    queryFn: () =>
      apiFetch<{ items: ProductSalesMetric[] }>(`/admin/dashboard/products${qs(params)}`),
    enabled,
  })
}

export function useAdminProductDashboards(
  params: Omit<AdminDashboardParams, 'productId' | 'currency'> & { productIds: string[] },
  enabled = true,
) {
  return useQueries({
    queries: params.productIds.map((productId) => {
      const query = {
        ...(params.participantId ? { participantId: params.participantId } : {}),
        ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
        ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        productId,
      }
      return {
        queryKey: ['admin-dashboard', query],
        queryFn: () => apiFetch<AdminDashboard>(`/admin/dashboard${qs(query)}`),
        enabled,
      }
    }),
  })
}

export function useAdminSalesInfinite(params: {
  q?: string
  participantId?: string
  status?: string
  productId?: string
  currency?: string
  dateFrom?: string
  dateTo?: string
  includeFunnel?: boolean
}) {
  return useInfiniteQuery({
    queryKey: ['admin-sales-infinite', params],
    queryFn: ({ pageParam }) =>
      apiFetch<Page<SaleItem>>(`/admin/sales${qs({ ...params, cursor: pageParam ?? undefined })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export function useAdminSale(saleId: string | null) {
  return useQuery({
    queryKey: ['admin-sale', saleId],
    queryFn: () => apiFetch<{ sale: SaleItem | null }>(`/admin/sales/${saleId}`),
    refetchInterval: (query) =>
      query.state.data?.sale?.status === 'REFUND_REQUESTED' ? 3000 : false,
    enabled: Boolean(saleId),
  })
}

export function useAdminSaleJourney(journeyId: string | null) {
  return useQuery({
    queryKey: ['admin-sale-journey', journeyId],
    queryFn: async () => {
      const page = await apiFetch<Page<SaleItem>>(
        `/admin/sales${qs({ q: journeyId ?? undefined, includeFunnel: true, limit: 100 })}`,
      )
      return page.items.filter(
        (item) => (item.journeyId ?? item.originSaleId ?? item.id) === journeyId,
      )
    },
    enabled: Boolean(journeyId),
  })
}

export type LedgerTotals = {
  count: number
  amount: MoneyDto
  pendingCount: number
  pendingAmount: MoneyDto
}

export function useAdminReceivablesInfinite(params: {
  recipientId?: string
  saleId?: string
  currency?: string
  dateFrom?: string
  dateTo?: string
}) {
  return useInfiniteQuery({
    queryKey: ['admin-receivables-infinite', params],
    queryFn: ({ pageParam }) =>
      apiFetch<Page<LedgerItem>>(
        `/admin/receivables${qs({ ...params, cursor: pageParam ?? undefined })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export function useAdminReceivablesSummary(
  params: {
    recipientId?: string
    saleId?: string
    currency?: string
    dateFrom?: string
    dateTo?: string
  },
  enabled = true,
) {
  return useQuery({
    queryKey: ['admin-receivables-summary', params],
    queryFn: () => apiFetch<LedgerTotals>(`/admin/receivables/summary${qs(params)}`),
    enabled,
  })
}

export type HourlyReceivablePoint = {
  hour: string
  amount: MoneyDto
  count: number
}

export type HourlyReceivables = {
  currency: string
  items: HourlyReceivablePoint[]
}

export function useAdminChargebackPenalties(
  params: { currency: string; dateFrom: string; dateTo: string; recipientId?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['admin-chargeback-penalties', params],
    queryFn: () => apiFetch<{ count: number }>(`/admin/chargeback-penalties${qs(params)}`),
    enabled,
  })
}

export function useAdminReceivablesHourly(
  params: { currency: string; dateFrom: string; dateTo: string; recipientId?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['admin-receivables-hourly', params],
    queryFn: () => apiFetch<HourlyReceivables>(`/admin/receivables/hourly${qs(params)}`),
    enabled,
  })
}

export function useAdminReceivableCurrencies(
  params: { recipientId?: string; dateFrom?: string; dateTo?: string } = {},
) {
  return useQuery({
    queryKey: ['admin-receivable-currencies', params],
    queryFn: () =>
      apiFetch<{ items: CurrencySummaryItem[] }>(`/admin/receivables/currencies${qs(params)}`),
  })
}

export function useAdminReceivablesBySale(saleId: string | null) {
  return useQuery({
    queryKey: ['admin-receivables-by-sale', saleId],
    queryFn: () => apiFetch<{ items: LedgerItem[] }>(`/admin/receivables/by-sale/${saleId}`),
    enabled: Boolean(saleId),
  })
}

export function useAdminBalance(userId: string) {
  return useQuery({
    queryKey: ['admin-balance', userId],
    queryFn: () => apiFetch<{ items: BalanceItem[] }>(`/admin/users/${userId}/balance`),
    enabled: userId.length > 0,
  })
}

export function useAdminStatementInfinite(
  userId: string,
  params: { currency?: string; view?: AdminStatementView; dateFrom?: string; dateTo?: string } = {},
) {
  return useInfiniteQuery({
    queryKey: ['admin-statement-infinite', userId, params],
    queryFn: ({ pageParam }) =>
      apiFetch<Page<AdminStatementItem>>(
        `/admin/users/${userId}/statement${qs({ ...params, presentation: params.view === 'journal' ? 'movements' : undefined, cursor: pageParam ?? undefined })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: userId.length > 0,
  })
}

export function useAdminSubscriptionsInfinite(params: {
  dateFrom?: string
  dateTo?: string
  q?: string
  participantId?: string
  status?: string
}) {
  return useInfiniteQuery({
    queryKey: ['admin-subscriptions-infinite', params],
    queryFn: async ({ pageParam, signal }) => {
      const page = await apiFetch<
        Page<SubscriptionItem> & {
          appliedDateRange?: { dateFrom: string | null; dateTo: string | null }
        }
      >(`/admin/subscriptions${qs({ ...params, cursor: pageParam ?? undefined })}`, { signal })
      if (
        (params.dateFrom || params.dateTo) &&
        (page.appliedDateRange?.dateFrom !== (params.dateFrom ?? null) ||
          page.appliedDateRange?.dateTo !== (params.dateTo ?? null))
      )
        throw new Error('SUBSCRIPTION_DATE_FILTER_UNSUPPORTED')
      return page
    },
    retry: (failureCount, error) =>
      error.message !== 'SUBSCRIPTION_DATE_FILTER_UNSUPPORTED' && failureCount < 1,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export function useAdminRuntimeCapabilities(enabled = true) {
  return useQuery({
    queryKey: ['admin-runtime-capabilities'],
    queryFn: () => apiFetch<AdminRuntimeCapabilities>('/admin/runtime-capabilities'),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useSubscriptionTestContext(subscriptionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin-subscription-test', subscriptionId],
    queryFn: () =>
      apiFetch<SubscriptionTestContext>(
        `/admin/qa/recurrence/subscriptions/${encodeURIComponent(subscriptionId)}`,
      ),
    enabled: enabled && subscriptionId.length > 0,
  })
}

export function advanceSubscriptionTest(subscriptionId: string, expectedInvoiceId: string) {
  return apiFetch<SubscriptionTestResult>(
    `/admin/qa/recurrence/subscriptions/${encodeURIComponent(subscriptionId)}/advance`,
    {
      method: 'POST',
      body: { expectedInvoiceId },
    },
  )
}

export function useAdminProducts(userId: string) {
  return useQuery({
    queryKey: ['admin-products', userId],
    queryFn: () => apiFetch<{ items: ProductItem[] }>(`/admin/users/${userId}/products`),
    enabled: userId.length > 0,
  })
}

export function useAdminUserLookup(query: string) {
  return useQuery({
    queryKey: ['admin-user-lookup', query],
    queryFn: () =>
      apiFetch<{ user: AdminLookupUser | null }>(`/admin/users/lookup${qs({ q: query })}`),
    enabled: query.length > 0,
  })
}

export function useAdminUsersLookup(queries: string[]) {
  const normalized = useMemo(() => {
    return [...new Set(queries.map((query) => query.trim()).filter(Boolean))].sort()
  }, [queries])

  const results = useQueries({
    queries: normalized.map((query) => ({
      queryKey: ['admin-user-lookup', query],
      queryFn: () =>
        apiFetch<{ user: AdminLookupUser | null }>(`/admin/users/lookup${qs({ q: query })}`),
      enabled: query.length > 0,
      staleTime: 5 * 60 * 1000,
    })),
  })

  const usersById = useMemo(() => {
    const users = new Map<string, AdminLookupUser>()
    for (const result of results) {
      const user = result.data?.user
      if (user) {
        users.set(user.id, user)
      }
    }
    return users
  }, [results])

  return {
    usersById,
    isLoading: results.some((result) => result.isLoading || result.isFetching),
  }
}

export function useAdminUserSearch(query: string) {
  const normalized = query.trim()
  return useQuery({
    queryKey: ['admin-user-search', normalized],
    queryFn: () =>
      apiFetch<{ items: AdminLookupUser[] }>(
        `/admin/users/search${qs({ q: normalized, limit: 8 })}`,
      ),
    enabled: normalized.length >= 2,
  })
}

export function useAdminUsers(enabled: boolean, limit = 50) {
  return useInfiniteQuery({
    queryKey: ['admin-users', limit],
    queryFn: ({ pageParam }) =>
      apiFetch<{ items: AdminLookupUser[]; nextOffset: number | null }>(
        `/admin/users${qs({
          limit,
          offset: pageParam,
        })}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    enabled,
  })
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => apiFetch<{ user: AdminUserDetail }>(`/admin/users/${userId}`),
    enabled: Boolean(userId),
  })
}

export type AdminKycQueue = 'support' | 'seller' | 'stripe_review'

export function useAdminKycActionRequiredInfinite(queue: AdminKycQueue = 'support') {
  return useInfiniteQuery({
    queryKey: ['admin-kyc-action-required', queue],
    queryFn: async ({ pageParam }) =>
      AdminKycActionRequiredPageSchema.parse(
        await apiFetch<unknown>(
          `/admin/kyc/action-required${qs({
            cursor: pageParam ?? undefined,
            limit: 50,
            queue,
            olderThanDays: queue === 'stripe_review' ? 3 : undefined,
          })}`,
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
  })
}

export function useSyncAdminKyc(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () =>
      AdminKycSyncResultSchema.parse(
        await apiFetch<unknown>(`/admin/users/${userId}/kyc/sync`, {
          method: 'POST',
          body: {},
          cache: 'no-store',
        }),
      ),
    onSuccess: async (result) => {
      queryClient.setQueryData(['admin-kyc-verification', userId], result.verification)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-user', userId], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['admin-kyc-action-required'] }),
      ])
    },
  })
}

export function useAdminAcquirers(enabled = true) {
  return useQuery({
    queryKey: ['admin-acquirers'],
    queryFn: () => apiFetch<{ provider: 'STRIPE'; items: AcquirerAccount[] }>('/admin/acquirers'),
    enabled,
  })
}

export function updateAcquirer(
  accountId: string,
  body: Partial<{
    name: string
  }>,
) {
  return apiFetch<{ account: AcquirerAccount }>(`/admin/acquirers/${accountId}`, {
    method: 'PATCH',
    body,
  })
}

export function testAcquirer(accountId: string) {
  return apiFetch<{ account: AcquirerAccount }>(`/admin/acquirers/${accountId}/test`, {
    method: 'POST',
  })
}

export function reconcileAcquirerWebhook(accountId: string) {
  return apiFetch<{ account: AcquirerAccount }>(`/admin/acquirers/${accountId}/webhook/reconcile`, {
    method: 'POST',
  })
}

export function reconcileAcquirerWallets(accountId: string) {
  return apiFetch<{ account: AcquirerAccount }>(`/admin/acquirers/${accountId}/wallets/reconcile`, {
    method: 'POST',
  })
}

export function updateAcquirerStatus(accountId: string, status: 'ACTIVE' | 'PAUSED' | 'DISABLED') {
  return apiFetch<{ account: AcquirerAccount }>(`/admin/acquirers/${accountId}/status`, {
    method: 'PATCH',
    body: { status },
  })
}

export function useAdminProduct(productId: string) {
  return useQuery({
    queryKey: ['admin-product', productId],
    queryFn: () => apiFetch<{ product: ProductItem }>(`/admin/products/${productId}`),
    enabled: productId.length > 0,
  })
}

export function useAdminDivergencesInfinite(params: { status?: string; saleId?: string }) {
  return useInfiniteQuery({
    queryKey: ['admin-divergences-infinite', params],
    queryFn: ({ pageParam }) =>
      apiFetch<Page<DivergenceItem>>(
        `/admin/reconciliation/divergences${qs({ ...params, cursor: pageParam ?? undefined })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export function useAccountingOverview() {
  return useQuery({
    queryKey: ['admin-accounting-overview'],
    queryFn: () => apiFetch<AccountingOverview>('/admin/accounting/overview'),
    refetchInterval: 30_000,
  })
}

export function useAccountingTransactionsInfinite(
  params: {
    q?: string
    sourceType?: string
    currency?: string
    participantId?: string
    saleId?: string
  },
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ['admin-accounting-transactions', params],
    queryFn: ({ pageParam, signal }) =>
      apiFetch<Page<AccountingTransaction>>(
        `/admin/accounting/transactions${qs({ ...params, cursor: pageParam ?? undefined })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
  })
}

export function useAccountingTransaction(transactionId: string | null) {
  return useQuery({
    queryKey: ['admin-accounting-transaction', transactionId],
    queryFn: ({ signal }) =>
      apiFetch<{ transaction: AccountingTransactionDetail | null }>(
        `/admin/accounting/transactions/${encodeURIComponent(transactionId ?? '')}`,
        { signal },
      ),
    enabled: transactionId !== null,
  })
}

export function useAccountingTrialBalance(
  params: { currency?: string; participantId?: string },
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ['admin-accounting-trial-balance', params],
    queryFn: ({ pageParam, signal }) =>
      apiFetch<Page<AccountingBalance>>(
        `/admin/accounting/trial-balance${qs({ ...params, cursor: pageParam ?? undefined })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
  })
}

export function useAdminOperationalAlertsInfinite(params: {
  status?: string
  severity?: string
  key?: string
}) {
  return useInfiniteQuery({
    queryKey: ['admin-operational-alerts-infinite', params],
    queryFn: ({ pageParam }) =>
      apiFetch<
        Page<OperationalAlertItem> & {
          summary: OperationalAlertSummary
        }
      >(`/admin/operational-alerts${qs({ ...params, cursor: pageParam ?? undefined })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export function acknowledgeOperationalAlert(alertId: string) {
  return apiFetch<{ alert: OperationalAlertItem | null }>(
    `/admin/operational-alerts/${alertId}/acknowledge`,
    {
      method: 'POST',
    },
  )
}

export function resolveOperationalAlert(alertId: string, body: { note?: string }) {
  return apiFetch<{ alert: OperationalAlertItem | null }>(
    `/admin/operational-alerts/${alertId}/resolve`,
    {
      method: 'POST',
      body,
    },
  )
}

export function useAdminGatewayWebhooksInfinite(params: { status?: string; gateway?: string }) {
  return useInfiniteQuery({
    queryKey: ['admin-gateway-webhooks-infinite', params],
    queryFn: ({ pageParam }) =>
      apiFetch<Page<GatewayWebhookItem>>(
        `/admin/gateway-webhooks${qs({ ...params, cursor: pageParam ?? undefined })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export function retryGatewayWebhook(webhookId: string) {
  return apiFetch<{ webhook: GatewayWebhookItem | null }>(
    `/admin/gateway-webhooks/${webhookId}/retry`,
    {
      method: 'POST',
    },
  )
}

export function useAdminDeadEventsInfinite() {
  return useInfiniteQuery({
    queryKey: ['admin-events-infinite', { status: 'DEAD' }],
    queryFn: ({ pageParam }) =>
      apiFetch<Page<AdminEventItem>>(
        `/admin/events${qs({ status: 'DEAD', cursor: pageParam ?? undefined })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export function retryAdminEvent(eventId: string) {
  return apiFetch<{ event: Pick<AdminEventItem, 'id' | 'type'> | null }>(
    `/admin/events/${encodeURIComponent(eventId)}/retry`,
    { method: 'POST' },
  )
}

export async function requestAdminSaleRefund(saleId: string, body: { reason?: string }) {
  const response = await apiFetch<{ sale: SaleItem | null }>(`/admin/sales/${saleId}/refund`, {
    method: 'POST',
    body,
  })
  if (response.sale === null) {
    throw new Error('Venda não encontrada.')
  }
  return response.sale
}

export async function cancelAdminSubscription(subscriptionId: string, body: { reason: string }) {
  const response = await apiFetch<{ subscription: SubscriptionItem | null }>(
    `/admin/subscriptions/${subscriptionId}/cancel`,
    { method: 'POST', body },
  )
  if (response.subscription === null) {
    throw new Error('Assinatura não encontrada.')
  }
  return response.subscription
}

export function useAdminChargebackCases(status = '') {
  return useQuery({
    queryKey: ['admin-chargeback-cases', status],
    queryFn: () =>
      apiFetch<{ items: ChargebackCaseItem[] }>(
        `/admin/chargeback-cases${qs({ status: status || undefined })}`,
      ),
  })
}

export function manualMatchChargebackCase(
  item: Pick<ChargebackCaseItem, 'provider' | 'provider_event_id'>,
  body: { saleId: string; force?: boolean },
) {
  return apiFetch<{ case: ChargebackCaseItem }>(
    `/admin/chargeback-cases/${encodeURIComponent(item.provider)}/${encodeURIComponent(item.provider_event_id)}/manual-match`,
    { method: 'POST', body },
  )
}

export function dismissChargebackCase(
  item: Pick<ChargebackCaseItem, 'provider' | 'provider_event_id'>,
  body: { reason: string },
) {
  return apiFetch<{ case: ChargebackCaseItem }>(
    `/admin/chargeback-cases/${encodeURIComponent(item.provider)}/${encodeURIComponent(item.provider_event_id)}/dismiss`,
    { method: 'POST', body },
  )
}

export type CheckoutExperienceMode = 'FORCE_NEW' | 'FORCE_LEGACY' | 'RANDOM'

export type CheckoutExperienceMetrics = {
  variant: 'new' | 'legacy'
  viewed: number
  paid: number
  abandoned: number
  accessed: number
}

export type CheckoutExperienceSnapshot = {
  mode: CheckoutExperienceMode
  description: string
  updatedAt: string
  updatedBy?: string
  metrics: CheckoutExperienceMetrics[]
}

export function useAdminCheckoutExperience(enabled = true) {
  return useQuery({
    queryKey: ['admin-checkout-experience'],
    queryFn: () => apiFetch<CheckoutExperienceSnapshot>('/admin/checkout-experience'),
    enabled,
  })
}

export function updateCheckoutExperience(body: { mode: CheckoutExperienceMode }) {
  return apiFetch<CheckoutExperienceSnapshot>('/admin/checkout-experience', {
    method: 'PATCH',
    body,
  })
}

export function useAdminStaff() {
  return useQuery({
    queryKey: ['admin-staff'],
    queryFn: () => apiFetch<{ items: StaffItem[] }>('/admin/staff'),
  })
}

export function createAdminStaff(body: {
  email: string
  password: string
  name: string
  isSuper: boolean
  permissions: string[]
}) {
  return apiFetch<{ staff: StaffItem }>('/admin/staff', {
    method: 'POST',
    body,
  })
}

export function updateAdminStaffPermissions(
  staffId: string,
  body: { isSuper: boolean; permissions: string[] },
) {
  return apiFetch<{ staff: StaffItem }>(`/admin/staff/${staffId}/permissions`, {
    method: 'PATCH',
    body,
  })
}

export function updateAdminStaffStatus(staffId: string, status: StaffItem['status']) {
  return apiFetch<{ staff: StaffItem }>(`/admin/staff/${staffId}/status`, {
    method: 'PATCH',
    body: { status },
  })
}

export type ExportFormat = 'xlsx' | 'csv'

export function exportAdminSales(
  params: Record<string, string | string[] | boolean | undefined>,
  format: ExportFormat = 'xlsx',
): Promise<Blob> {
  return apiFetchBlob(`/admin/sales/export${qs({ ...params, format })}`)
}

export function exportAdminReceivables(
  params: Record<string, string | string[] | boolean | undefined>,
  format: ExportFormat = 'xlsx',
): Promise<Blob> {
  return apiFetchBlob(`/admin/receivables/export${qs({ ...params, format })}`)
}

export function exportAdminStatement(
  userId: string,
  params: Record<string, string | string[] | boolean | undefined>,
  format: ExportFormat = 'xlsx',
): Promise<Blob> {
  return apiFetchBlob(
    `/admin/users/${userId}/statement/export${qs({
      ...params,
      presentation: params.view === 'journal' ? 'movements' : undefined,
      format,
    })}`,
  )
}

export function exportAdminSubscriptions(
  params: Record<string, string | string[] | boolean | undefined>,
  format: ExportFormat = 'xlsx',
): Promise<Blob> {
  return apiFetchBlob(`/admin/subscriptions/export${qs({ ...params, format })}`)
}

export type FinancialQueryCacheSnapshot = {
  ttlSeconds: number
  updatedAt: string | null
  updatedBy?: string | null
  environment: string
}

export function useAdminFinancialQueryCache(enabled = true) {
  return useQuery({
    queryKey: ['admin-financial-query-cache'],
    queryFn: () => apiFetch<FinancialQueryCacheSnapshot>('/admin/financial-query-cache'),
    enabled,
  })
}

export function updateFinancialQueryCache(body: { ttlSeconds: number }) {
  return apiFetch<FinancialQueryCacheSnapshot>('/admin/financial-query-cache', {
    method: 'PATCH',
    body,
  })
}
